/** Shared contracts for Aetherloom — logic never depends on Three.js. */

export type PieceKind =
  | 'ember'
  | 'tide'
  | 'gale'
  | 'root'
  | 'solar'
  | 'moon'
  | 'void';

export const PIECE_KINDS: PieceKind[] = [
  'ember',
  'tide',
  'gale',
  'root',
  'solar',
  'moon',
  'void',
];

export type SpecialType =
  | 'beam' // 4-match: line clear (orientation H or V)
  | 'nova' // T/L intersection: radial blast
  | 'prism' // 5-match: clear all of one kind
  | 'burst'; // 2x2 or compact square: 3x3 explosion

export type BlockerType =
  | 'frost' // layered ice, 1–3 layers
  | 'seal' // locks cell until cracked
  | 'vine' // spreading substance
  | 'crate' // layered wood crate
  | 'rift'; // hazard that damages moves / expands

export type CellFlag =
  | 'hole'
  | 'portal'
  | 'conveyor'
  | 'oneway'
  | 'spawn'
  | 'exit'
  | 'locked';

export type ObjectiveType =
  | 'score'
  | 'collect'
  | 'clear_frost'
  | 'clear_crate'
  | 'clear_vine'
  | 'clear_tiles' // jelly-like under-tiles
  | 'drop' // drop relics to bottom
  | 'activate_specials'
  | 'combo_specials'
  | 'survive_rift'
  | 'clear_seals';

export type EnginePhase =
  | 'idle'
  | 'selected'
  | 'swapping'
  | 'resolving'
  | 'cascading'
  | 'spawning'
  | 'special_activation'
  | 'reshuffling'
  | 'checking_end'
  | 'won'
  | 'lost'
  | 'paused';

export interface Coord {
  r: number;
  c: number;
}

export interface PieceId {
  id: number;
  kind: PieceKind;
  special?: SpecialType;
  /** Beam orientation when special === 'beam' */
  beamAxis?: 'h' | 'v';
  /** Prism target kind (for color clear pieces) */
  prismKind?: PieceKind;
  /** Drop objective token attached */
  isRelic?: boolean;
  /** Frozen / cannot swap this turn */
  lockedTurns?: number;
}

export interface BlockerState {
  type: BlockerType;
  layers: number;
}

export interface UnderTile {
  layers: number; // e.g. luminous weave tiles to clear
}

export interface CellState {
  /** false = hole / non-playable */
  active: boolean;
  piece: PieceId | null;
  blocker: BlockerState | null;
  under: UnderTile | null;
  portalTo?: Coord;
  conveyorDir?: 'up' | 'down' | 'left' | 'right';
  oneWay?: 'up' | 'down' | 'left' | 'right';
  spawnAllowed: boolean;
  isExit: boolean; // drop zone
  sealed: boolean; // seal blocker flag
}

export interface ObjectiveDef {
  type: ObjectiveType;
  target: number;
  kind?: PieceKind;
  special?: SpecialType;
  label: string;
}

export interface ObjectiveProgress {
  type: ObjectiveType;
  target: number;
  current: number;
  kind?: PieceKind;
  special?: SpecialType;
  label: string;
  done: boolean;
}

export interface LevelDef {
  id: number;
  worldId: number;
  name: string;
  moves: number;
  rows: number;
  cols: number;
  pieceSet: PieceKind[];
  objectives: ObjectiveDef[];
  /** Sparse map: key "r,c" -> partial cell overrides */
  layout?: Record<string, PartialCellLayout>;
  /** Pre-placed pieces: key "r,c" -> kind or special */
  prefill?: Record<string, PrefillCell>;
  spawnWeights?: Partial<Record<PieceKind, number>>;
  forbidInitialMatches?: boolean;
  tutorial?: string[];
  starScores?: [number, number, number];
  difficulty: 1 | 2 | 3 | 4 | 5;
  /** Seed for deterministic generation */
  seed: number;
}

export interface PartialCellLayout {
  hole?: boolean;
  portalTo?: Coord;
  conveyorDir?: 'up' | 'down' | 'left' | 'right';
  oneWay?: 'up' | 'down' | 'left' | 'right';
  noSpawn?: boolean;
  exit?: boolean;
  blocker?: BlockerState;
  under?: UnderTile;
  sealed?: boolean;
}

export interface PrefillCell {
  kind?: PieceKind;
  special?: SpecialType;
  beamAxis?: 'h' | 'v';
  isRelic?: boolean;
}

export interface WorldDef {
  id: number;
  name: string;
  subtitle: string;
  theme: WorldTheme;
  levelIds: number[];
  unlockAfter?: number; // previous world final level id
}

export type WorldTheme =
  | 'ember'
  | 'tide'
  | 'gale'
  | 'root'
  | 'void';

export interface BoardEvent {
  type: BoardEventType;
  payload: Record<string, unknown>;
}

export type BoardEventType =
  | 'swap'
  | 'swap_invalid'
  | 'match'
  | 'clear'
  | 'fall'
  | 'spawn'
  | 'special_created'
  | 'special_activated'
  | 'special_combo'
  | 'blocker_damaged'
  | 'under_cleared'
  | 'objective_progress'
  | 'score'
  | 'cascade'
  | 'reshuffle'
  | 'win'
  | 'lose'
  | 'phase'
  | 'relic_dropped'
  | 'vine_spread'
  | 'rift_tick';

export interface MatchGroup {
  cells: Coord[];
  kind: PieceKind;
  pattern: 'line' | 'square' | 'intersect' | 'five' | 'long';
  specialToCreate?: SpecialType;
  beamAxis?: 'h' | 'v';
  /** Preferred cell for special spawn (usually swap destination) */
  specialAt?: Coord;
}

export interface ClearResult {
  cells: Coord[];
  score: number;
  specialsCreated: Array<{ at: Coord; special: SpecialType; kind: PieceKind; beamAxis?: 'h' | 'v' }>;
  specialsActivated: Array<{ at: Coord; special: SpecialType }>;
  blockersDamaged: Array<{ at: Coord; type: BlockerType; layersLeft: number }>;
  underCleared: Coord[];
  collected: Partial<Record<PieceKind, number>>;
  relicsDropped: number;
  comboDepth: number;
}

export interface SimStats {
  wins: number;
  losses: number;
  avgMovesLeft: number;
  avgCascades: number;
  avgSpecials: number;
  deadBoards: number;
  avgScore: number;
  objectiveRates: Record<string, number>;
  runs: number;
}

export interface SaveData {
  version: number;
  unlockedLevel: number;
  levelStars: Record<number, number>;
  levelBestScore: Record<number, number>;
  levelBestMovesLeft: Record<number, number>;
  settings: GameSettings;
  totalScore: number;
  chaptersCompleted: number[];
  tutorialsSeen: string[];
  lastPlayedLevel?: number;
}

export interface GameSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  colorblind: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
  reducedFlash: boolean;
  uiScale: number;
  hints: boolean;
  confirmSpecials: boolean;
  quality: 'low' | 'medium' | 'high';
}

export const DEFAULT_SETTINGS: GameSettings = {
  masterVolume: 0.8,
  musicVolume: 0.55,
  sfxVolume: 0.75,
  colorblind: false,
  highContrast: false,
  reducedMotion: false,
  reducedFlash: false,
  uiScale: 1,
  hints: true,
  confirmSpecials: false,
  quality: 'high',
};

export const DEFAULT_SAVE: SaveData = {
  version: 1,
  unlockedLevel: 1,
  levelStars: {},
  levelBestScore: {},
  levelBestMovesLeft: {},
  settings: { ...DEFAULT_SETTINGS },
  totalScore: 0,
  chaptersCompleted: [],
  tutorialsSeen: [],
};

export function keyOf(r: number, c: number): string {
  return `${r},${c}`;
}

export function parseKey(k: string): Coord {
  const [r, c] = k.split(',').map(Number);
  return { r, c };
}

export function coordsEqual(a: Coord, b: Coord): boolean {
  return a.r === b.r && a.c === b.c;
}

export function isAdjacent(a: Coord, b: Coord): boolean {
  return Math.abs(a.r - b.r) + Math.abs(a.c - b.c) === 1;
}
